import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import React from 'react';
import AdminUser from '../../models/admin/AdminUser';
import { ChatParticipant } from '../../services/admin/FidesChatAdminService';
import { formatAdminSearchLabel, panelAdminExternalId } from './fidesChatPanelUtils';

interface FidesChatTeamSectionProps {
  teamSectionExpanded: boolean;
  onToggleTeamSection: () => void;
  participantsLoading: boolean;
  isUnowned: boolean;
  effectiveOwnerName: string | null;
  participants: ChatParticipant[];
  isOwner: boolean;
  openConversationId: string | null;
  isClaiming: boolean;
  onClaim: () => void;
  onRemoveParticipant: (adminExternalId: string) => void;
  canInviteAdmins: boolean;
  isAddingParticipant: boolean;
  inviteSearchOptions: AdminUser[];
  adminSearchLoading: boolean;
  inviteSearchInput: string;
  onInviteSearchInputChange: (value: string) => void;
  onInviteAdmin: (admin: AdminUser | null) => void;
  adminSearchOptions: AdminUser[];
  transferOwnerValue: AdminUser | null;
  transferSearchInput: string;
  onTransferSearchInputChange: (value: string) => void;
  onTransferOwnerValueChange: (admin: AdminUser | null) => void;
  isTransferring: boolean;
  onTransferOwnership: () => void;
}

const FidesChatTeamSection: React.FC<FidesChatTeamSectionProps> = ({
  teamSectionExpanded,
  onToggleTeamSection,
  participantsLoading,
  isUnowned,
  effectiveOwnerName,
  participants,
  isOwner,
  openConversationId,
  isClaiming,
  onClaim,
  onRemoveParticipant,
  canInviteAdmins,
  isAddingParticipant,
  inviteSearchOptions,
  adminSearchLoading,
  inviteSearchInput,
  onInviteSearchInputChange,
  onInviteAdmin,
  adminSearchOptions,
  transferOwnerValue,
  transferSearchInput,
  onTransferSearchInputChange,
  onTransferOwnerValueChange,
  isTransferring,
  onTransferOwnership,
}) => (
  <Box sx={{ px: 2, py: 1, backgroundColor: '#fafafa', borderBottom: '1px solid #eee' }}>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        cursor: 'pointer',
        userSelect: 'none',
        py: 0.5,
      }}
      onClick={onToggleTeamSection}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          Team
        </Typography>
        {!participantsLoading && (
          <Typography variant="caption" color="text.secondary" noWrap display="block">
            {isUnowned ? 'Unassigned' : `Owner: ${effectiveOwnerName ?? 'Unknown'}`}
            {participants.length > 1 && ` · ${participants.length} admins`}
          </Typography>
        )}
      </Box>
      {participantsLoading && <CircularProgress size={14} />}
      <IconButton
        size="small"
        aria-label={teamSectionExpanded ? 'Collapse team' : 'Expand team'}
        onClick={(event) => {
          event.stopPropagation();
          onToggleTeamSection();
        }}
      >
        {teamSectionExpanded ? (
          <ExpandLessIcon fontSize="small" />
        ) : (
          <ExpandMoreIcon fontSize="small" />
        )}
      </IconButton>
    </Box>

    <Collapse in={teamSectionExpanded}>
      {participantsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
          <CircularProgress size={18} />
        </Box>
      ) : (
        <Box sx={{ pt: 0.5, pb: 0.5 }}>
          {isUnowned && (
            <Typography variant="body2" sx={{ mb: 1 }}>
              Unassigned — any admin can reply; fallback email goes to all subscribed admins.
            </Typography>
          )}
          {isUnowned && openConversationId && (
            <Button
              size="small"
              variant="outlined"
              disabled={isClaiming}
              onClick={onClaim}
              sx={{ mb: 1 }}
            >
              {isClaiming ? <CircularProgress size={14} /> : 'Take ownership'}
            </Button>
          )}
          {participants.length > 0 && (
            <List dense disablePadding sx={{ mb: 1 }}>
              {participants.map((participant) => (
                <ListItem
                  key={participant.adminExternalId}
                  disableGutters
                  secondaryAction={
                    isOwner && !participant.isOwner ? (
                      <Button
                        size="small"
                        color="inherit"
                        onClick={() => onRemoveParticipant(participant.adminExternalId)}
                      >
                        Remove
                      </Button>
                    ) : undefined
                  }
                >
                  <ListItemText
                    primary={participant.adminName ?? participant.adminExternalId}
                    secondary={participant.isOwner ? 'Owner' : 'Participant'}
                  />
                </ListItem>
              ))}
            </List>
          )}

          {openConversationId && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 0.5 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                Invite admin
              </Typography>
              {isUnowned ? (
                <Typography variant="body2" color="text.secondary">
                  Take ownership first to invite other admins.
                </Typography>
              ) : !canInviteAdmins ? (
                <Typography variant="body2" color="text.secondary">
                  Only admins on this chat can invite others.
                </Typography>
              ) : (
                <Autocomplete
                  size="small"
                  freeSolo={false}
                  clearOnBlur
                  selectOnFocus
                  handleHomeEndKeys
                  disabled={isAddingParticipant}
                  options={inviteSearchOptions}
                  loading={adminSearchLoading}
                  inputValue={inviteSearchInput}
                  onInputChange={(_event, value, reason) => {
                    if (reason === 'reset') return;
                    onInviteSearchInputChange(value);
                  }}
                  onChange={(_event, value) => onInviteAdmin(value)}
                  getOptionLabel={formatAdminSearchLabel}
                  isOptionEqualToValue={(left, right) =>
                    panelAdminExternalId(left) === panelAdminExternalId(right)
                  }
                  filterOptions={(options) => options}
                  noOptionsText={
                    inviteSearchInput.trim().length < 2
                      ? 'Type at least 2 characters to search'
                      : 'No admins found'
                  }
                  slotProps={{
                    popper: { sx: { zIndex: 1400 } },
                  }}
                  renderOption={(props, option) => (
                    <li {...props} key={panelAdminExternalId(option)}>
                      <ListItemText
                        primary={formatAdminSearchLabel(option)}
                        secondary={option.email ?? undefined}
                      />
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Search by name or email…"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {isAddingParticipant || adminSearchLoading ? (
                              <CircularProgress color="inherit" size={18} sx={{ mr: 1 }} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              )}

              {canInviteAdmins && (
                <>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                    sx={{ mt: 0.5 }}
                  >
                    Transfer ownership
                  </Typography>
                  <Autocomplete
                    size="small"
                    freeSolo={false}
                    options={adminSearchOptions}
                    value={transferOwnerValue}
                    inputValue={transferSearchInput}
                    onInputChange={(_event, value, reason) => {
                      if (reason === 'reset') return;
                      onTransferSearchInputChange(value);
                    }}
                    onChange={(_event, value) => onTransferOwnerValueChange(value)}
                    getOptionLabel={formatAdminSearchLabel}
                    isOptionEqualToValue={(left, right) =>
                      panelAdminExternalId(left) === panelAdminExternalId(right)
                    }
                    filterOptions={(options) => options}
                    loading={adminSearchLoading}
                    noOptionsText={
                      transferSearchInput.trim().length < 2
                        ? 'Type at least 2 characters to search'
                        : 'No admins found'
                    }
                    slotProps={{
                      popper: { sx: { zIndex: 1400 } },
                    }}
                    renderInput={(params) => (
                      <TextField {...params} placeholder="Search admin to transfer to…" />
                    )}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={isTransferring ? <CircularProgress size={14} /> : <SwapHorizIcon />}
                    disabled={
                      !transferOwnerValue ||
                      !panelAdminExternalId(transferOwnerValue) ||
                      isTransferring
                    }
                    onClick={onTransferOwnership}
                  >
                    Transfer ownership
                  </Button>
                </>
              )}
            </Box>
          )}
        </Box>
      )}
    </Collapse>
  </Box>
);

export default FidesChatTeamSection;
